/**
 * PostComments - Aura Action composer for comments. Mirrors AddPostForm's toolbox:
 * @mentions, #hashtags, 📍 locations with inline aura pills to pre-tag aura before sending.
 * Daily aura indicator. After comment is created, pending aura is sent against the new comment_id.
 */

import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { createEditor, Transforms, Editor } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { selectUser } from '../../store/auth.reducer';
import { selectDailyStats, decrementRemaining, setDailyStats } from '../../store/aura.reducer';
import { getComments, addComment } from '../../helpers/postsHelpers';
import { searchUsers, searchHashtags, searchLocations } from '../../helpers/searchHelpers';
import { createAura, getUserDailyPointStats } from '../../helpers/auraHelpers';
import AuraCategoriesModal from '../Aura/AuraCategoriesModal';
import CommentContent from './CommentContent';
import UserAvatar from '../Profile/UserAvatar';
import NavIcon from '../Layout/NavIcon';
import toast from 'react-hot-toast';

const initialValue = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

const INLINE_VOID_TYPES = ['mention', 'hashtag', 'location'];
const FALLBACK_KEYBOARD_CLEARANCE_PX = 320;
const SCROLL_CONTAINER_TOP_OFFSET_PX = 16;
const KEYBOARD_SCROLL_DELAY_MS = 450;

const getPendingAuraTotal = (items) =>
  (items || []).reduce((sum, item) => sum + (Number(item.points) || 1), 0);

const buildDailyStatsFromSentTotal = (dailyLimit, pointsSentToday) => ({
  dailyLimit,
  dailySent: pointsSentToday,
  remainingToday: Math.max(0, dailyLimit - pointsSentToday),
});

function measureKeyboardClearance() {
  const viewport = window.visualViewport;
  if (!viewport) return 0;
  return Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
}

function findScrollContainer(element) {
  let node = element?.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') return node;
    node = node.parentElement;
  }
  return null;
}

function getScrollBehavior() {
  if (!window.matchMedia) return 'smooth';
  return window.matchMedia('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth';
}

function scrollInputToTopOfScrollContainer(input, offsetPx = SCROLL_CONTAINER_TOP_OFFSET_PX) {
  const behavior = getScrollBehavior();
  const container = findScrollContainer(input);
  if (!container) {
    input.scrollIntoView?.({ block: 'start', behavior });
    return;
  }
  const containerRect = container.getBoundingClientRect();
  const inputRect = input.getBoundingClientRect();
  const targetScrollTop = container.scrollTop + inputRect.top - containerRect.top - offsetPx;
  const nextScrollTop = Math.max(0, targetScrollTop);
  if (typeof container.scrollTo === 'function') {
    container.scrollTo({ top: nextScrollTop, behavior });
  } else {
    container.scrollTop = nextScrollTop;
  }
}

function isCommentKeyboardField(target) {
  return target instanceof HTMLElement && !!target.closest('[data-comment-editor="true"]');
}

/** Serialize Slate value to plain text and extract structured data. Root paragraphs become `\n`. */
export function serializeComment(value) {
  let content = '';
  const mentionIds = [];
  let location = null;

  const walk = (nodes, isRoot = false) => {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      if (isRoot && i > 0) content += '\n';
      if (node.type === 'mention') {
        content += `@${node.username} `;
        if (node.user_id) mentionIds.push(node.user_id);
      } else if (node.type === 'hashtag') {
        content += `#${node.hashtag} `;
      } else if (node.type === 'location') {
        content += `📍 ${node.place_name} `;
        if (!location && node.place_name) {
          location = {
            place_name: node.place_name,
            short_description: node.short_description || null,
            google_place_id: node.google_place_id || null,
          };
          if (node.location_id) location.location_id = node.location_id;
        }
      } else if (node.children) {
        walk(node.children, false);
      } else if (node.text !== undefined) {
        content += node.text;
      }
    }
  };
  walk(value, true);
  return {
    content: content.trim(),
    mentionIds,
    location,
  };
}

function PostComments({ postId }) {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const dailyStats = useSelector(selectDailyStats);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editor] = useState(() => {
    const base = withReact(createEditor());
    const { isInline, isVoid } = base;
    base.isInline = (element) =>
      (element?.type && INLINE_VOID_TYPES.includes(element.type)) || isInline(element);
    base.isVoid = (element) =>
      (element?.type && INLINE_VOID_TYPES.includes(element.type)) || isVoid(element);
    return base;
  });
  const [value, setValue] = useState(initialValue);
  const [slateKey, setSlateKey] = useState(0);
  const [dropdown, setDropdown] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [keyboardFieldFocused, setKeyboardFieldFocused] = useState(false);
  const [keyboardClearancePx, setKeyboardClearancePx] = useState(0);
  const [pendingAura, setPendingAura] = useState([]);
  const [auraPickerTarget, setAuraPickerTarget] = useState(null);
  const composerRef = useRef(null);
  const dropdownRef = useRef(null);
  const keyboardFocusedInputRef = useRef(null);
  const keyboardUserIntentRef = useRef(false);

  const updateKeyboardClearance = useCallback(() => {
    const measured = measureKeyboardClearance();
    setKeyboardClearancePx(measured > 0 ? measured : FALLBACK_KEYBOARD_CLEARANCE_PX);
  }, []);

  const handleComposerPointerDown = useCallback((e) => {
    if (isCommentKeyboardField(e.target)) keyboardUserIntentRef.current = true;
  }, []);

  const handleComposerFocus = useCallback((e) => {
    if (!isCommentKeyboardField(e.target)) return;
    keyboardUserIntentRef.current = false;
    keyboardFocusedInputRef.current = e.target.closest('[data-comment-editor="true"]');
    setKeyboardFieldFocused(true);
    updateKeyboardClearance();
  }, [updateKeyboardClearance]);

  const handleComposerBlur = useCallback((e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setKeyboardFieldFocused(false);
    setKeyboardClearancePx(0);
    keyboardFocusedInputRef.current = null;
  }, []);

  useEffect(() => {
    if (!keyboardFieldFocused) return undefined;
    let firstFrame = null;
    let secondFrame = null;
    const timeout = window.setTimeout(() => {
      firstFrame = window.requestAnimationFrame(() => {
        secondFrame = window.requestAnimationFrame(() => {
          if (keyboardFocusedInputRef.current) {
            scrollInputToTopOfScrollContainer(keyboardFocusedInputRef.current);
          }
        });
      });
    }, KEYBOARD_SCROLL_DELAY_MS);
    return () => {
      window.clearTimeout(timeout);
      if (firstFrame != null) window.cancelAnimationFrame(firstFrame);
      if (secondFrame != null) window.cancelAnimationFrame(secondFrame);
    };
  }, [keyboardFieldFocused, keyboardClearancePx]);

  useEffect(() => {
    if (!keyboardFieldFocused) return undefined;
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    viewport.addEventListener('resize', updateKeyboardClearance);
    viewport.addEventListener('scroll', updateKeyboardClearance);
    return () => {
      viewport.removeEventListener('resize', updateKeyboardClearance);
      viewport.removeEventListener('scroll', updateKeyboardClearance);
    };
  }, [keyboardFieldFocused, updateKeyboardClearance]);

  useEffect(() => {
    let cancelled = false;
    getComments(postId)
      .then((data) => {
        if (!cancelled) setComments(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setComments([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [postId]);

  const getTriggerState = useCallback(() => {
    const { selection } = editor;
    if (!selection) return null;
    const path = selection.anchor.path;
    try {
      if (!Editor.hasPath(editor, path)) {
        Transforms.deselect(editor);
        return null;
      }
      const [node] = Editor.node(editor, path);
      const text = node?.text ?? '';
      const offset = selection.anchor.offset;
      const before = text.slice(0, offset);
      const atMatch = before.match(/@([a-zA-Z0-9_]*)$/);
      const hashMatch = before.match(/#([a-zA-Z0-9_]*)$/);
      const pinMatch = before.match(/📍\s*(.*)$/);
      if (atMatch) return { type: 'mention', query: atMatch[1], triggerLen: atMatch[0].length, path: [...path], offset };
      if (hashMatch) return { type: 'hashtag', query: hashMatch[1], triggerLen: hashMatch[0].length, path: [...path], offset };
      if (pinMatch) return { type: 'location', query: pinMatch[1].trim(), triggerLen: pinMatch[0].length, path: [...path], offset };
    } catch {
      Transforms.deselect(editor);
      return null;
    }
    return null;
  }, [editor]);

  useLayoutEffect(() => {
    if (editor.selection) {
      try {
        if (!Editor.hasPath(editor, editor.selection.anchor.path)) {
          Transforms.deselect(editor);
        }
      } catch {
        Transforms.deselect(editor);
      }
    }
  }, [value, editor]);

  useEffect(() => {
    const state = getTriggerState();
    if (!state) {
      setDropdown(null);
      return;
    }
    setDropdown({ ...state, items: [], loading: true });
  }, [value, getTriggerState]);

  useEffect(() => {
    if (!dropdown) return;
    const { type, query } = dropdown;
    let cancelled = false;
    const load = async () => {
      let items = [];
      if (type === 'mention') items = await searchUsers(query).catch(() => []);
      else if (type === 'hashtag') items = await searchHashtags(query).catch(() => []);
      else if (type === 'location') items = await searchLocations(query).catch(() => []);
      if (!cancelled) {
        setDropdown((d) => (d ? { ...d, items, loading: false } : null));
      }
    };
    load();
    return () => { cancelled = true; };
  }, [dropdown?.type, dropdown?.query]); // eslint-disable-line react-hooks/exhaustive-deps

  const insertMention = useCallback(
    (mentioned) => {
      const state = getTriggerState();
      if (!state || state.type !== 'mention') return;
      const { path, offset, triggerLen } = state;
      const start = { path: [...path], offset: Math.max(0, offset - triggerLen) };
      const end = { path: [...path], offset };
      Transforms.delete(editor, { at: { anchor: start, focus: end } });
      Transforms.insertNodes(
        editor,
        [
          { type: 'mention', user_id: mentioned.user_id, username: mentioned.username, children: [{ text: '' }] },
          { text: ' ' },
        ],
        { at: start }
      );
      try {
        const endPos = Editor.end(editor, [0]);
        Transforms.select(editor, endPos);
      } catch {
        Transforms.deselect(editor);
      }
      setDropdown(null);
    },
    [editor, getTriggerState]
  );

  const insertHashtag = useCallback(
    (tag) => {
      const state = getTriggerState();
      if (!state || state.type !== 'hashtag') return;
      const { path, offset, triggerLen } = state;
      const start = { path: [...path], offset: Math.max(0, offset - triggerLen) };
      const end = { path: [...path], offset };
      Transforms.delete(editor, { at: { anchor: start, focus: end } });
      const tagData = typeof tag === 'string' ? { hashtag: tag } : tag;
      Transforms.insertNodes(
        editor,
        [
          {
            type: 'hashtag',
            hashtag: tagData.hashtag,
            hashtag_id: tagData.hashtag_id ?? null,
            children: [{ text: '' }],
          },
          { text: ' ' },
        ],
        { at: start }
      );
      try {
        const endPos = Editor.end(editor, [0]);
        Transforms.select(editor, endPos);
      } catch {
        Transforms.deselect(editor);
      }
      setDropdown(null);
    },
    [editor, getTriggerState]
  );

  const insertLocation = useCallback(
    (loc) => {
      const state = getTriggerState();
      if (!state || state.type !== 'location') return;
      const { path, offset, triggerLen } = state;
      const start = { path: [...path], offset: Math.max(0, offset - triggerLen) };
      const end = { path: [...path], offset };
      Transforms.delete(editor, { at: { anchor: start, focus: end } });
      Transforms.insertNodes(
        editor,
        [
          {
            type: 'location',
            location_id: loc.location_id,
            place_name: loc.place_name,
            short_description: loc.short_description || null,
            google_place_id: loc.google_place_id || null,
            children: [{ text: '' }],
          },
          { text: ' ' },
        ],
        { at: start }
      );
      try {
        const endPos = Editor.end(editor, [0]);
        Transforms.select(editor, endPos);
      } catch {
        Transforms.deselect(editor);
      }
      setDropdown(null);
    },
    [editor, getTriggerState]
  );

  const insertLocationTrigger = useCallback(() => {
    ReactEditor.focus(editor);
    let insertAt;
    try {
      insertAt = editor.selection ? editor.selection.anchor : Editor.end(editor, [0]);
      if (!Editor.hasPath(editor, insertAt.path)) {
        insertAt = Editor.end(editor, [0]);
      }
    } catch {
      insertAt = Editor.end(editor, [0]);
    }
    Transforms.insertText(editor, '📍 ', { at: insertAt });
    const afterPos = { path: insertAt.path, offset: insertAt.offset + 2 };
    Transforms.select(editor, { anchor: afterPos, focus: afterPos });
    setActionMenuOpen(false);
    keyboardFocusedInputRef.current = composerRef.current?.querySelector('[data-comment-editor="true"]') || null;
    setKeyboardFieldFocused(true);
    updateKeyboardClearance();
  }, [editor, updateKeyboardClearance]);

  const queuePendingAura = useCallback((target, entries) => {
    setPendingAura((prev) => [
      ...prev,
      ...entries.map(({ categoryId, points }) => ({
        targetId: target.targetId,
        targetType: target.targetType,
        categoryId,
        hashtag: target.targetType === 'hashtag' ? target.hashtag ?? null : null,
        placeName: target.targetType === 'location' ? target.placeName ?? null : null,
        points,
      })),
    ]);
    setAuraPickerTarget(null);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const { content, mentionIds, location } = serializeComment(value);
    if (!content.trim()) return;
    setSubmitting(true);
    setAuraPickerTarget(null);
    try {
      let latestStats = dailyStats;
      const queuedAuraTotal = getPendingAuraTotal(pendingAura);
      if (queuedAuraTotal > 0) {
        latestStats = await getUserDailyPointStats();
        dispatch(setDailyStats(latestStats));
        if (queuedAuraTotal > latestStats.remainingToday) {
          toast.error('Not enough aura points left today for this comment.');
          return;
        }
      }

      const comment = await addComment(postId, content, mentionIds, location);
      for (const { targetId, targetType, categoryId, hashtag, placeName, points } of pendingAura) {
        let resolvedTargetId = targetId;
        if (targetType === 'hashtag' && !resolvedTargetId && hashtag && comment.hashtags) {
          const match = comment.hashtags.find((h) => h.hashtag?.toLowerCase() === hashtag.toLowerCase());
          resolvedTargetId = match?.hashtag_id;
        }
        if (targetType === 'location' && !resolvedTargetId && placeName && comment.location?.place_name) {
          if (
            comment.location.place_name === placeName ||
            comment.location.place_name?.toLowerCase() === placeName?.toLowerCase()
          ) {
            resolvedTargetId = comment.location.location_id;
          }
        }
        if (resolvedTargetId) {
          const auraPoints = points ?? 1;
          try {
            const auraResult = await createAura({
              targetId: resolvedTargetId,
              targetType,
              commentId: comment.comment_id,
              categoryId,
              points: auraPoints,
            });
            if (typeof auraResult?.pointsSentToday === 'number' && latestStats?.dailyLimit != null) {
              dispatch(setDailyStats(buildDailyStatsFromSentTotal(latestStats.dailyLimit, auraResult.pointsSentToday)));
            } else {
              dispatch(decrementRemaining(auraPoints));
            }
          } catch {
            toast.error('Some aura could not be sent.');
          }
        }
      }
      setPendingAura([]);
      setComments((c) => [
        ...c,
        {
          ...comment,
          username: user?.username,
          displayname: user?.displayname,
          profile_pic_url: user?.profile_pic_url,
          default_profile_color: user?.default_profile_color,
        },
      ]);
      setValue(initialValue);
      setSlateKey((k) => k + 1);
    } catch {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const renderElement = useCallback(
    ({ attributes, children, element }) => {
      const renderTargetWithAura = (label, targetId, targetType, fallbackKey = null, tagClass = 'text-ether') => {
        const isOwn = targetType === 'user' && user?.user_id === targetId;
        const hasIdOrTag = targetId != null || (targetType === 'hashtag' && fallbackKey) || (targetType === 'location' && fallbackKey);
        const key = `${targetType}:${targetId ?? fallbackKey}`;
        const pendingCount = pendingAura.reduce(
          (n, p) =>
            `${p.targetType}:${p.targetId ?? p.hashtag ?? p.placeName}` === key
              ? n + (p.points ?? 1)
              : n,
          0
        );

        return (
          <span {...attributes} className="inline-flex items-center gap-1" contentEditable={false}>
            <span className={`${tagClass} font-medium`}>{label}</span>
            {hasIdOrTag && !isOwn && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() =>
                  setAuraPickerTarget({
                    targetId,
                    targetType,
                    hashtag: targetType === 'hashtag' ? fallbackKey : undefined,
                    placeName: targetType === 'location' ? fallbackKey : undefined,
                  })
                }
                className="inline-flex items-center justify-center gap-1 w-6 h-6 rounded-full text-xs bg-aura/25 text-aura hover:bg-aura/40 border border-aura/50 shadow-glow transition-colors"
                title="Add aura"
                aria-label={`Add aura to ${label.trim()}`}
              >
                <span aria-hidden="true">✦</span>
                {pendingCount > 0 && <span>{pendingCount}</span>}
              </button>
            )}
            {children}
          </span>
        );
      };

      switch (element.type) {
        case 'mention':
          return renderTargetWithAura(`@${element.username} `, element.user_id, 'user', null, 'text-ether');
        case 'hashtag':
          return renderTargetWithAura(`#${element.hashtag} `, element.hashtag_id, 'hashtag', element.hashtag, 'text-ether');
        case 'location':
          return renderTargetWithAura(`📍 ${element.place_name} `, element.location_id, 'location', element.place_name, 'text-mist');
        default:
          return <p {...attributes}>{children}</p>;
      }
    },
    [user?.user_id, pendingAura]
  );

  const hasContent = (nodes) => {
    for (const n of nodes || []) {
      if (n.type === 'mention' || n.type === 'hashtag' || n.type === 'location') return true;
      if (n.text?.trim()) return true;
      if (n.children && hasContent(n.children)) return true;
    }
    return false;
  };
  const isEmpty = !hasContent(value);

  if (loading) return <p className="text-silver text-sm">Loading comments...</p>;

  return (
    // flex-col-reverse keeps the composer visually at the top of the list so
    // the comment <form> below stays last in the DOM (preserving natural tab
    // order) while rendering above the rendered comment list.
    <div className="flex flex-col-reverse gap-3">
      <ul className="space-y-3">
        {comments.map((c) => {
          const commenterName = c.displayname || c.username || '?';
          const profileLink = c.username ? `/profile/${encodeURIComponent(c.username)}` : null;
          const avatar = (
            <UserAvatar
              user={c}
              className="w-8 h-8 rounded-full flex-shrink-0"
              emojiClassName="text-sm"
            />
          );
          return (
            <li key={c.comment_id} className="flex gap-2 text-sm">
              {profileLink ? (
                <Link to={profileLink} className="flex-shrink-0">
                  {avatar}
                </Link>
              ) : (
                avatar
              )}
              <div className="flex-1 min-w-0">
                {profileLink ? (
                  <Link to={profileLink} className="font-medium text-ash hover:text-ether">
                    {commenterName}
                  </Link>
                ) : (
                  <span className="font-medium text-ash">{commenterName}</span>
                )}{' '}
                <CommentContent comment={c} />
              </div>
            </li>
          );
        })}
      </ul>
      <form
        ref={composerRef}
        onSubmit={handleSubmit}
        onPointerDown={handleComposerPointerDown}
        onFocus={handleComposerFocus}
        onBlur={handleComposerBlur}
        className="space-y-2"
        data-testid="comment-composer"
      >
        {dailyStats != null && (
          <div
            className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-aura/10 border border-aura/30 text-aura text-xs shadow-glow w-fit"
            title="Aura you can give today"
          >
            <span className="opacity-90">✦</span>
            <span>You have {dailyStats.remainingToday} aura left today</span>
          </div>
        )}
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <div className="bg-shadow rounded-lg px-3 py-2 min-h-[80px] focus-within:ring-2 focus-within:ring-ether/50">
              <Slate
                key={slateKey}
                editor={editor}
                initialValue={initialValue}
                onChange={setValue}
              >
                <Editable
                  placeholder="Add a comment... Use @ for people, # for things, 📍 for places"
                  renderElement={renderElement}
                  renderLeaf={({ attributes, children }) => <span {...attributes}>{children}</span>}
                  data-comment-editor="true"
                  onKeyDown={(e) => {
                    if (dropdown && e.key === 'Escape') setDropdown(null);
                    if (e.key === ' ' || e.key === 'Enter') {
                      const state = getTriggerState();
                      if (state?.type === 'hashtag' && state.query?.trim()) {
                        e.preventDefault();
                        insertHashtag(state.query.trim());
                        return;
                      }
                    }
                    // Explicit break so Return creates a new line and never submits the form (iOS WKWebView).
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (e.shiftKey) {
                        Editor.insertSoftBreak(editor);
                      } else {
                        Editor.insertBreak(editor);
                      }
                    }
                  }}
                  className="text-ash placeholder-silver min-h-[60px] outline-none text-base md:text-sm"
                />
              </Slate>
            </div>
            {dropdown && (
              <div
                ref={dropdownRef}
                className="absolute left-0 right-0 top-full mt-1 z-50 bg-charcoal rounded-xl shadow-soft border border-shadow py-2 max-h-48 overflow-y-auto"
              >
                {dropdown.loading ? (
                  <p className="px-3 py-2 text-silver text-sm">Searching...</p>
                ) : dropdown.items.length === 0 ? (
                  <div className="py-2">
                    <p className="px-3 py-1 text-silver text-sm">
                      {dropdown.type === 'mention' && 'No users found'}
                      {dropdown.type === 'hashtag' && (dropdown.query?.trim() ? 'No hashtags found' : 'Type a hashtag')}
                      {dropdown.type === 'location' && 'No locations found'}
                    </p>
                    {dropdown.type === 'hashtag' && dropdown.query?.trim() && (
                      <button
                        type="button"
                        onClick={() => insertHashtag(dropdown.query.trim())}
                        className="w-full px-3 py-2 text-left text-ether hover:bg-shadow flex items-center gap-2"
                      >
                        <span>Create</span>
                        <span className="font-medium">#{dropdown.query.trim()}</span>
                      </button>
                    )}
                  </div>
                ) : (
                  dropdown.items.map((item, idx) => (
                    <button
                      key={dropdown.type === 'mention' ? item.user_id : dropdown.type === 'hashtag' ? item.hashtag_id : (item.location_id ?? item.google_place_id ?? item.place_name ?? idx)}
                      type="button"
                      onClick={() => {
                        if (dropdown.type === 'mention') insertMention(item);
                        else if (dropdown.type === 'hashtag') insertHashtag(item);
                        else if (dropdown.type === 'location') insertLocation(item);
                      }}
                      className="w-full px-3 py-2 text-left text-ash hover:bg-shadow flex items-center gap-2"
                    >
                      {dropdown.type === 'mention' && (
                        <>
                          <UserAvatar
                            user={item}
                            className="w-8 h-8 rounded-full"
                            emojiClassName="text-sm"
                          />
                          <span>{item.displayname || item.username}</span>
                          <span className="text-silver text-sm">@{item.username}</span>
                        </>
                      )}
                      {dropdown.type === 'hashtag' && (
                        <span className="text-ether">#{item.hashtag}</span>
                      )}
                      {dropdown.type === 'location' && (
                        <>
                          <span className="text-mist">📍</span>
                          <span>{item.place_name}</span>
                          {item.short_description && (
                            <span className="text-silver text-sm truncate">{item.short_description}</span>
                          )}
                        </>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-between items-center flex-wrap gap-2">
          <div className="relative">
            <button
              type="button"
              onClick={() => setActionMenuOpen((open) => !open)}
              className="w-9 h-9 rounded-full bg-shadow border border-shadow hover:bg-shadow/80 hover:border-ether/30 flex items-center justify-center text-silver hover:text-ether transition-colors"
              title="Add options"
              aria-label="Add options"
              aria-expanded={actionMenuOpen}
            >
              <NavIcon icon="plus" className="w-5 h-5" />
            </button>
            {actionMenuOpen && (
              <div className="absolute left-0 bottom-full mb-2 z-50 w-56 bg-charcoal rounded-xl shadow-soft border border-shadow py-2">
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertLocationTrigger();
                  }}
                  className="w-full px-3 py-2 text-left text-ash hover:bg-shadow flex items-center gap-3"
                >
                  <NavIcon icon="location-pin" className="w-5 h-5 text-mist" />
                  <span>Map pin</span>
                </button>
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={submitting || isEmpty}
            className="px-4 py-2 rounded-xl bg-aura text-void text-sm font-medium disabled:opacity-50"
          >
            {submitting ? 'Sending...' : 'Send aura'}
          </button>
        </div>
        <div
          aria-hidden="true"
          data-testid="comment-keyboard-spacer"
          className={`md:hidden shrink-0 ${keyboardFieldFocused ? 'transition-all duration-300 ease-zen' : ''}`}
          style={{ height: keyboardFieldFocused ? keyboardClearancePx : 0 }}
        />
      </form>
      <AuraCategoriesModal
        open={auraPickerTarget != null}
        onClose={() => setAuraPickerTarget(null)}
        onRadiate={({ entries }) => {
          if (auraPickerTarget) queuePendingAura(auraPickerTarget, entries);
        }}
        radiateLabel="Add Aura"
        title="Add Aura"
      />
    </div>
  );
}

export default PostComments;
